/**
 * Cell-click adapter for the Validation Step Breakdown pivot. Pure,
 * non-mutating module that rewrites the Applied_Filter in response to
 * a Pivot_Cell click.
 *
 * Only the Row_Group_Path components of a click (productLineId,
 * programId, country, asinType) are pushed into the filter group.
 * The step name itself is never emitted as a filter token because
 * DACS has no server-side grammar for nested-field filtering; it
 * lives in the pivot's local `activeCell` state instead and is used
 * purely for the "active cell" visual indicator.
 *
 * Drill-down-authored tokens are tagged with
 * `internalSource: "step-drilldown"` so a subsequent click can strip
 * exactly the tokens it authored without touching a user-authored
 * chip that happens to share the same (propertyKey, value). The
 * marker is session-scoped: `sumPropertyFilterQueries` ignores extra
 * fields, so it never reaches the server or the URL. On a page
 * reload, pre-existing tokens come back unmarked and are treated as
 * user tokens, matching the pivot's reset-on-queryString-change
 * effect.
 *
 * `isStepFilterToken` remains exported so callers can defensively
 * strip stale step-name tokens (from URLs or Redux state that
 * predates this change) whenever the filter is rewritten.
 */

import type {
  PropertyFilter,
  PropertyFilterGroup,
} from "../../searchSideBar/searchSideBarConnector";
import {
  rebuildAttributeRoot,
  splitAttributeFilters,
} from "../../searchSideBar/singleSelectFilterToken";
import type {
  RowGroupLevel,
  RowGroupPath,
  ValidationPipeline,
} from "./stepBreakdown";

const STEP_PROPERTY_KEY_LVS = "validationDetailResultLVS.detail.step";
const STEP_PROPERTY_KEY_HATTRICK = "validationDetailResultHattrick.detail.step";

const STEP_PROPERTY_KEYS: readonly string[] = [
  STEP_PROPERTY_KEY_LVS,
  STEP_PROPERTY_KEY_HATTRICK,
];

const STEP_DRILLDOWN_MARKER = "step-drilldown" as const;

const ROW_GROUP_LEVEL_PROPERTY_KEY: Partial<Record<RowGroupLevel, string>> = {
  productLine: "productLineId",
  program: "programId",
  marketplace: "country",
  asinType: "asinType",
};

export interface StepFilterClick {
  readonly stepName: string;
  readonly pipeline: ValidationPipeline;
  readonly path: RowGroupPath;
}

interface TargetPathToken {
  readonly propertyKey: string;
  readonly value: string;
}

export function isStepFilterToken(filter: PropertyFilter): boolean {
  return STEP_PROPERTY_KEYS.includes(filter.propertyKey);
}

function isStepDrilldownToken(filter: PropertyFilter): boolean {
  return filter.internalSource === STEP_DRILLDOWN_MARKER;
}

function targetPathTokensFor(path: RowGroupPath): readonly TargetPathToken[] {
  const tokens: TargetPathToken[] = [];
  for (const pathToken of path) {
    const propertyKey = ROW_GROUP_LEVEL_PROPERTY_KEY[pathToken.level];
    if (propertyKey !== undefined) {
      tokens.push({ propertyKey, value: pathToken.value });
    }
  }
  return tokens;
}

function isPropertyFilter(
  f: PropertyFilter | PropertyFilterGroup
): f is PropertyFilter {
  return f.type === "PropertyFilter";
}

/**
 * Returns a fresh `PropertyFilterGroup` with the step drill-down's
 * path tokens replaced by tokens derived from `nextPath`. `current`
 * is never mutated.
 *
 * Behavior:
 * - Every stale step-name token (propertyKey ending in
 *   `.detail.step`) is stripped as cleanup. These may exist in old
 *   URLs from before step-name filtering was removed.
 * - Every token previously authored by the drill-down (identified by
 *   `internalSource: "step-drilldown"`) is stripped, regardless of
 *   propertyKey or value.
 * - Fresh drill-down tokens are appended from `nextPath`, each
 *   marked with the same `internalSource`. When `nextPath` is
 *   `undefined` or empty, nothing is appended and the resulting
 *   group has no drill-down applied.
 * - All user-authored filters pass through unchanged — including any
 *   that happen to share a (propertyKey, value) with a drill-down
 *   token. A user chip and a drill-down chip on the same key are
 *   allowed to coexist so a toggle-off can't clobber the manual
 *   chip.
 */
export function applyStepDrilldownFilters(
  current: PropertyFilterGroup,
  nextPath: RowGroupPath | undefined
): PropertyFilterGroup {
  const { asinFilter, attributeFilters, groupOperation } =
    splitAttributeFilters(current);

  const strippedNestedFilters: Array<PropertyFilter | PropertyFilterGroup> = [];
  for (const f of attributeFilters) {
    if (isPropertyFilter(f)) {
      if (isStepFilterToken(f)) {
        continue;
      }
      if (isStepDrilldownToken(f)) {
        continue;
      }
    }
    strippedNestedFilters.push(f);
  }

  const nextTokens = targetPathTokensFor(nextPath ?? []);
  const appendedPathFilters: PropertyFilter[] = nextTokens.map((t) => ({
    type: "PropertyFilter",
    operator: "filter",
    propertyKey: t.propertyKey,
    value: t.value,
    internalSource: STEP_DRILLDOWN_MARKER,
  }));

  const nextNestedFilters = [...strippedNestedFilters, ...appendedPathFilters];

  return rebuildAttributeRoot(
    current,
    asinFilter,
    groupOperation,
    nextNestedFilters
  );
}
